import type { GuestBrowserCapability } from './guestBrowserCapability';

/**
 * Why the host wants an IDR. The split that matters is whether a decodability requirement
 * stands behind the request - without the frame, the guest cannot paint - or whether the
 * keyframe is cosmetic, taken to clear a decoder or land a change cleanly.
 *
 * - `startup`: answer generated / ICE up / fully ready. Nothing paints without it.
 * - `track-replace`: a new source track (ShadowCam swap). The encoder restarts on new content.
 * - `client`: the guest asked (startup retries until its first decoded frame, or the stall
 *   step after 1.5 s without a frame). Only the guest knows it is blind; always honoured.
 * - `recovery-climb`: the one IDR a within-tier climb takes once the queue has drained,
 *   to hand the decoder a clean reference. Cosmetic.
 * - `floor-release`: a `tier360 -> hard` release lands the resolution change on a keyframe.
 *   Cosmetic: the encoder emits a keyframe on a resolution change regardless.
 * - `loss-burst-end`: one IDR after a wireless-loss burst, so the guest can paint the
 *   next clean picture. Cosmetic on an engine whose jitter buffer an IDR inflates.
 *   Android Chrome is that engine: a loss burst there drops frames continuously, and
 *   the IDR ratchets the jitter-buffer target for minutes. Retransmission heals the
 *   hole. Safari and desktop Chromium still take the frame.
 */
export type KeyframeReason = 'startup' | 'track-replace' | 'client' | 'recovery-climb' | 'floor-release' | 'loss-burst-end';

/** The reasons no decodability requirement stands behind - the ones a gate may withhold. */
export type CosmeticKeyframeReason = Extract<KeyframeReason, 'recovery-climb' | 'floor-release' | 'loss-burst-end'>;

/** Reasons a decodability requirement stands behind. Never withheld on any engine. */
const REQUIRED_REASONS: ReadonlySet<KeyframeReason> = new Set<KeyframeReason>(['startup', 'track-replace', 'client']);

/** Minimum interval between cosmetic recovery keyframes for one peer. */
export const RECOVERY_KEYFRAME_MIN_INTERVAL_MS = 3_000;

export type KeyframeDecision = { send: true } | { send: false; withheld: string };

/**
 * Whether to send a keyframe for `reason` to a guest with `capability`.
 *
 * Required reasons always go. Cosmetic ones go unless the guest's engine pays for every
 * IDR with a jitter-buffer target that stays inflated for minutes
 * (`keyframeInflatesJitterBuffer`), in which case the keyframe costs more standing latency
 * than the clean reference it buys. An unknown engine (no clientStats yet) sends: the
 * guest is more likely blind than measured at that point. `framesDropped` is kept so
 * callers can still pass the burst count; a positive count does not admit a cosmetic
 * IDR on an engine whose jitter buffer it inflates.
 */
export function decideKeyframe(
  reason: KeyframeReason,
  capability: Pick<GuestBrowserCapability, 'keyframeInflatesJitterBuffer'> | undefined,
  unrecoveredFrames = 0,
): KeyframeDecision {
  if (REQUIRED_REASONS.has(reason)) return { send: true };
  if (reason === 'loss-burst-end' && unrecoveredFrames <= 0) {
    return { send: false, withheld: 'loss burst recovered without missing frames' };
  }
  if (capability?.keyframeInflatesJitterBuffer) {
    if (reason === 'loss-burst-end' && unrecoveredFrames > 0) return { send: true };
    return { send: false, withheld: 'keyframe inflates jitter-buffer target on this engine' };
  }
  return { send: true };
}

/** Required keyframes bypass throttling; cosmetic recovery keyframes share one clock gate. */
export function isRecoveryKeyframeThrottled(
  reason: KeyframeReason,
  nowMs: number,
  lastRecoveryKeyframeAtMs: number | undefined,
  minimumIntervalMs = RECOVERY_KEYFRAME_MIN_INTERVAL_MS,
): boolean {
  if (isRequiredKeyframeReason(reason) || lastRecoveryKeyframeAtMs === undefined) return false;
  if (![nowMs, lastRecoveryKeyframeAtMs, minimumIntervalMs].every(Number.isFinite)) return true;
  return nowMs - lastRecoveryKeyframeAtMs < Math.max(0, minimumIntervalMs);
}

export function isRequiredKeyframeReason(reason: KeyframeReason): boolean {
  return REQUIRED_REASONS.has(reason);
}
